/**
 * Security Provider Component
 *
 * Initializes and manages security features for the application
 */

import React, { useEffect, useState, createContext, useContext } from "react";
import {
  performSecurityHealthCheck,
  logSecurityEvent,
} from "../utils/securityInit";

interface SecurityContextType {
  isSecurityInitialized: boolean;
  securityHealthy: boolean;
  performHealthCheck: () => void;
}

const SecurityContext = createContext<SecurityContextType | undefined>(
  undefined
);

export const useSecurityContext = () => {
  const context = useContext(SecurityContext);
  if (!context) {
    throw new Error(
      "useSecurityContext must be used within a SecurityProvider"
    );
  }
  return context;
};

interface SecurityProviderProps {
  children: React.ReactNode;
}

export const SecurityProvider: React.FC<SecurityProviderProps> = ({
  children,
}) => {
  const [isSecurityInitialized, setIsSecurityInitialized] = useState(false);
  const [securityHealthy, setSecurityHealthy] = useState(false);

  const performHealthCheck = () => {
    const healthCheck = performSecurityHealthCheck();
    setSecurityHealthy(healthCheck.healthy);

    if (!healthCheck.healthy) {
      // Only log as warning, not error, since CSRF token may still be initializing
      console.warn("🔒 Security health check failed:", healthCheck.issues);

      // Only log security event for critical issues, not missing CSRF token during startup
      const criticalIssues = healthCheck.issues.filter(
        issue => !issue.includes("CSRF token")
      );
      if (criticalIssues.length > 0) {
        logSecurityEvent(
          "suspicious_activity",
          `Security health check failed: ${criticalIssues.join(", ")}`,
          "medium",
          { checks: healthCheck.checks, issues: criticalIssues }
        );
      }
    }
  };

  useEffect(() => {
    let healthCheckInterval: NodeJS.Timeout | null = null;
    let securityCheckInterval: NodeJS.Timeout | null = null;

    // Simple polling to check if security is initialized globally
    const checkSecurityState = () => {
      if (
        typeof window !== "undefined" &&
        window.__adapteach_security_initialized
      ) {
        setIsSecurityInitialized(true);
        performHealthCheck();

        // Set up periodic health checks
        if (!healthCheckInterval) {
          healthCheckInterval = setInterval(performHealthCheck, 5 * 60 * 1000);
        }

        // Stop checking since we found security is initialized
        if (securityCheckInterval) {
          clearInterval(securityCheckInterval);
          securityCheckInterval = null;
        }
      } else {
        // Security not yet initialized, will check again
      }
    };

    // Check immediately
    checkSecurityState();

    // If not initialized yet, poll every 100ms until it is
    if (!isSecurityInitialized) {
      securityCheckInterval = setInterval(checkSecurityState, 100);
    }

    // Cleanup function
    return () => {
      if (healthCheckInterval) {
        clearInterval(healthCheckInterval);
        healthCheckInterval = null;
      }
      if (securityCheckInterval) {
        clearInterval(securityCheckInterval);
        securityCheckInterval = null;
      }
    };
  }, []); // Empty dependency array - run only once on mount

  // Show loading state while security is initializing
  if (!isSecurityInitialized) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Initializing security features...</p>
        </div>
      </div>
    );
  }

  const contextValue: SecurityContextType = {
    isSecurityInitialized,
    securityHealthy,
    performHealthCheck,
  };

  return (
    <SecurityContext.Provider value={contextValue}>
      {children}
    </SecurityContext.Provider>
  );
};
